import { createContext, useContext, useEffect, useRef, type KeyboardEvent, type MutableRefObject, type ReactNode } from 'react';
import { revealTab } from './revealTab';
import { tabKeyboardTarget } from './tabKeyboardTarget';
import { TAB_VARIANTS, VARIANT_CLASSES, type TabVariant, type VariantClasses } from './tabVariants';

export interface TabDefinition<T extends string = string> {
    id: T;
    label: string;
}

interface TabsRootProps<T extends string> {
    tabs: TabDefinition<T>[];
    /** Must be an id in `tabs`; `children` must render from that same value. An unmatched
     * value normalises chrome and ARIA to the first tab as a defensive fallback only. */
    activeTab: T;
    onTabChange: (id: T) => void;
    /** Namespaces the generated tab and panel ids so two tablists can coexist on one page. */
    idPrefix: string;
    ariaLabel: string;
    /** `sub` is the segmented control for a tablist nested inside a `lift` tab panel. */
    variant?: TabVariant;
    /** Set only when no panel body holds a focusable element; APG then requires the
     *  panel itself to be a tab stop so keyboard users can reach its content. */
    focusablePanel?: boolean;
    children: ReactNode;
}

interface TabsContextValue {
    tabs: TabDefinition[];
    selectedIndex: number;
    onTabChange: (id: string) => void;
    ariaLabel: string;
    focusablePanel: boolean;
    classes: VariantClasses;
    panelId: string;
    tabId: (id: string) => string;
    buttonRefs: MutableRefObject<(HTMLButtonElement | null)[]>;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
    const context = useContext(TabsContext);
    if (!context) throw new Error('TabList and TabPanel must be rendered inside TabsRoot.');
    return context;
}

/** Shares tab state between a `TabList` and a `TabPanel` placed apart, e.g. a header and the page body. */
export function TabsRoot<T extends string>({
    tabs,
    activeTab,
    onTabChange,
    idPrefix,
    ariaLabel,
    variant = TAB_VARIANTS.lift,
    focusablePanel = false,
    children,
}: TabsRootProps<T>) {
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);

    const value: TabsContextValue = {
        tabs,
        selectedIndex: activeIndex < 0 ? 0 : activeIndex,
        onTabChange: onTabChange as (id: string) => void,
        ariaLabel,
        focusablePanel,
        classes: VARIANT_CLASSES[variant],
        panelId: `${idPrefix}-panel`,
        tabId: (id) => `${idPrefix}-tab-${id}`,
        buttonRefs,
    };

    return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

export function TabList() {
    const { tabs, selectedIndex, onTabChange, ariaLabel, classes, panelId, tabId, buttonRefs } = useTabsContext();
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        revealTab(listRef.current, buttonRefs.current[selectedIndex] ?? null);
    }, [selectedIndex, buttonRefs]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const focusedIndex = buttonRefs.current.indexOf(event.target as HTMLButtonElement);
        const from = focusedIndex < 0 ? selectedIndex : focusedIndex;
        const target = tabKeyboardTarget(event.key, from, tabs.length);
        if (target === null) return;
        event.preventDefault();
        onTabChange(tabs[target].id);
        buttonRefs.current[target]?.focus();
    };

    return (
        <div ref={listRef} role="tablist" aria-label={ariaLabel} className={classes.tablist} onKeyDown={handleKeyDown}>
            {tabs.map((tab, index) => {
                const isSelected = index === selectedIndex;
                const stateClass = isSelected ? classes.activeTab : classes.inactiveTab;
                return (
                    <button
                        key={tab.id}
                        ref={(node) => {
                            buttonRefs.current[index] = node;
                        }}
                        id={tabId(tab.id)}
                        type="button"
                        role="tab"
                        aria-selected={isSelected}
                        aria-controls={panelId}
                        tabIndex={isSelected ? 0 : -1}
                        className={`${classes.tab}${stateClass ? ` ${stateClass}` : ''}`}
                        onClick={() => onTabChange(tab.id)}
                    >
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
}

export function TabPanel({ children }: { children: ReactNode }) {
    const { tabs, selectedIndex, focusablePanel, panelId, tabId } = useTabsContext();

    return (
        <div
            id={panelId}
            role="tabpanel"
            aria-labelledby={tabs.length > 0 ? tabId(tabs[selectedIndex].id) : undefined}
            tabIndex={focusablePanel ? 0 : undefined}
            className="pt-2"
        >
            {children}
        </div>
    );
}

/** Tablist and panel together. Use `TabsRoot`, `TabList` and `TabPanel` when they sit apart. */
export function Tabs<T extends string>({ children, ...rootProps }: TabsRootProps<T>) {
    return (
        <TabsRoot {...rootProps}>
            <TabList />
            <TabPanel>{children}</TabPanel>
        </TabsRoot>
    );
}
